/**
 * Print output profiles and print shop presets.
 *
 * The profiles are the freely available ECI profiles (eci.org). The backend
 * image downloads them at build time, see infra/icc/fetch-profiles.sh.
 * Print shop presets only bundle their published defaults; the product data
 * sheet of the print shop always wins, so every value stays editable.
 */

export interface PrintProfile {
  id: string;
  label: string;
  /** File name inside ICC_DIR. */
  file: string;
  /** OutputConditionIdentifier of the ICC characterization registry. */
  outputCondition: string;
  /** Human readable description written into the PDF/X OutputIntent. */
  info: string;
  paper: "coated" | "uncoated";
  /** Maximum total area coverage built into the profile (percent). */
  tac: number;
}

export const PRINT_PROFILES = [
  {
    id: "pso-coated-v3",
    label: "PSO Coated v3 (FOGRA51)",
    file: "PSOcoated_v3.icc",
    outputCondition: "FOGRA51",
    info: "PSO Coated v3 – Offset, ISO 12647-2:2013, gestrichenes Papier (PT1/2)",
    paper: "coated",
    tac: 300,
  },
  {
    id: "pso-uncoated-v3",
    label: "PSO Uncoated v3 (FOGRA52)",
    file: "PSOuncoated_v3_FOGRA52.icc",
    outputCondition: "FOGRA52",
    info: "PSO Uncoated v3 – Offset, ISO 12647-2:2013, ungestrichenes Papier (PT5)",
    paper: "uncoated",
    tac: 300,
  },
  {
    id: "iso-coated-v2-300",
    label: "ISO Coated v2 300 % (FOGRA39)",
    file: "ISOcoated_v2_300_eci.icc",
    outputCondition: "FOGRA39",
    info: "ISO Coated v2 300% (ECI) – Offset, ISO 12647-2:2004, gestrichenes Papier",
    paper: "coated",
    tac: 300,
  },
  {
    id: "iso-coated-v2",
    label: "ISO Coated v2 (FOGRA39)",
    file: "ISOcoated_v2_eci.icc",
    outputCondition: "FOGRA39",
    info: "ISO Coated v2 (ECI) – Offset, ISO 12647-2:2004, gestrichenes Papier",
    paper: "coated",
    tac: 330,
  },
  {
    id: "pso-uncoated-iso12647",
    label: "PSO Uncoated ISO12647 (FOGRA47)",
    file: "PSO_Uncoated_ISO12647_eci.icc",
    outputCondition: "FOGRA47",
    info: "PSO Uncoated ISO12647 (ECI) – Offset, ungestrichenes Papier",
    paper: "uncoated",
    tac: 300,
  },
] as const satisfies readonly PrintProfile[];

export type PrintProfileId = (typeof PRINT_PROFILES)[number]["id"];
export const DEFAULT_PRINT_PROFILE: PrintProfileId = "pso-coated-v3";

export function getPrintProfile(id: string | undefined | null): PrintProfile {
  return PRINT_PROFILES.find((profile) => profile.id === id) ?? PRINT_PROFILES[0];
}

export const PDFX_STANDARDS = ["X-4", "X-3", "X-1a"] as const;
export type PdfxStandard = (typeof PDFX_STANDARDS)[number];

export const PDFX_LABELS: Record<PdfxStandard, string> = {
  "X-4": "PDF/X-4 (empfohlen, erlaubt Transparenzen)",
  "X-3": "PDF/X-3:2003 (ohne Transparenzen)",
  "X-1a": "PDF/X-1a:2001 (nur CMYK, ohne Transparenzen)",
};

export interface PrintProvider {
  id: string;
  name: string;
  /** Page with the print shop's data requirements. */
  url?: string;
  profiles: { coated: PrintProfileId; uncoated: PrintProfileId };
  bleedMm: number;
  safeMm: number;
  pdfx: PdfxStandard;
  cropMarks: boolean;
  /** Recommended minimum effective image resolution. */
  recommendedDpi: number;
  /** Below this resolution the export shows an error. */
  minimumDpi: number;
  /** Maximum total ink coverage in percent. */
  tac: number;
  /** Thinnest line that prints reliably (pt). */
  minLineWidthPt: number;
  /** Put small 100 % K text and thin black lines on overprint. */
  overprintBlack: boolean;
  notes: string[];
}

export const PRINT_PROVIDERS = [
  {
    id: "saxoprint",
    name: "SAXOPRINT",
    url: "https://www.saxoprint.de/service-und-hilfe/druckdatenanleitung",
    profiles: { coated: "pso-coated-v3", uncoated: "pso-uncoated-v3" },
    bleedMm: 2,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 250,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.125,
    overprintBlack: false,
    notes: [
      "Beschnitt je nach Produkt: Umschläge 1 mm, Kalender, Mappen und Werbetechnik 3 mm, Broschüren im Rollendruck 5 mm.",
      "Sicherheitsabstand zur Bindung (Kalender, Wire-O) 20 mm, zum Falz 5 mm.",
    ],
  },
  {
    id: "flyeralarm",
    name: "FLYERALARM",
    url: "https://www.flyeralarm.com/de/i/druckdaten/datenformat-und-beschnitt/",
    profiles: { coated: "iso-coated-v2-300", uncoated: "iso-coated-v2-300" },
    bleedMm: 1,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: true,
    notes: [
      "Beschnitt und Sicherheitsabstand stehen im Datenblatt des Produkts (meist 1 mm Beschnitt pro Seite).",
      "Keine Schnitt- oder Passermarken anlegen. Kleine schwarze Texte werden überdruckend angelegt.",
    ],
  },
  {
    id: "wirmachendruck",
    name: "WIRmachenDRUCK",
    url: "https://www.wir-machen-druck.de/druckdaten.html",
    profiles: { coated: "iso-coated-v2-300", uncoated: "iso-coated-v2-300" },
    bleedMm: 3,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [
      "WIRmachenDRUCK nennt PDF/X-1a als Standard. Für Dateien ohne Transparenzen kann das unter „Erweitert“ gewählt werden.",
    ],
  },
  {
    id: "onlineprinters",
    name: "Onlineprinters / diedruckerei.de",
    url: "https://www.onlineprinters.de/c/druckdaten/farbmodus-farbraum",
    profiles: { coated: "pso-coated-v3", uncoated: "pso-uncoated-v3" },
    bleedMm: 2,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [],
  },
  {
    id: "laserline",
    name: "Laserline",
    profiles: { coated: "pso-coated-v3", uncoated: "pso-uncoated-v3" },
    bleedMm: 2,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [],
  },
  {
    id: "viaprinto",
    name: "viaprinto",
    profiles: { coated: "iso-coated-v2", uncoated: "pso-uncoated-iso12647" },
    bleedMm: 3,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 330,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [],
  },
  {
    id: "print24",
    name: "print24",
    profiles: { coated: "iso-coated-v2", uncoated: "pso-uncoated-iso12647" },
    bleedMm: 2,
    safeMm: 3,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [],
  },
  {
    id: "custom",
    name: "Andere Druckerei (eigene Angaben)",
    profiles: { coated: "pso-coated-v3", uncoated: "pso-uncoated-v3" },
    bleedMm: 3,
    safeMm: 5,
    pdfx: "X-4",
    cropMarks: false,
    recommendedDpi: 300,
    minimumDpi: 150,
    tac: 300,
    minLineWidthPt: 0.25,
    overprintBlack: false,
    notes: [
      "Profil, Beschnitt, PDF/X-Version und Schnittmarken bitte dem Datenblatt der Druckerei entnehmen.",
    ],
  },
] as const satisfies readonly PrintProvider[];

export type PrintProviderId = (typeof PRINT_PROVIDERS)[number]["id"];
export const DEFAULT_PRINT_PROVIDER: PrintProviderId = "saxoprint";

/** Provider preset; no choice yet = default print shop, unknown ids = custom values. */
export function getPrintProvider(id: string | undefined | null): PrintProvider {
  const wanted = id ?? DEFAULT_PRINT_PROVIDER;
  return (
    PRINT_PROVIDERS.find((provider) => provider.id === wanted) ??
    PRINT_PROVIDERS.find((provider) => provider.id === "custom")!
  );
}

/** Profile to use for a document, from explicit choice or provider + paper. */
export function resolvePrintProfile(print: {
  provider?: string | undefined;
  paper?: "coated" | "uncoated" | undefined;
  profile?: string | undefined;
}): PrintProfile {
  if (print.profile && PRINT_PROFILES.some((profile) => profile.id === print.profile)) {
    return getPrintProfile(print.profile);
  }
  const provider = getPrintProvider(print.provider ?? DEFAULT_PRINT_PROVIDER);
  return getPrintProfile(provider.profiles[print.paper ?? "coated"]);
}

// ---------------------------------------------------------------------------
// Export options
// ---------------------------------------------------------------------------

export interface PrintExportSettings {
  provider: PrintProviderId | string;
  profile: PrintProfileId;
  bleedMm: number;
  pdfx: PdfxStandard;
  cropMarks: boolean;
  /** Rich black (e.g. 40/30/30/100) for large black areas instead of 100 % K. */
  richBlack: boolean;
  overprintBlack: boolean;
}

export const RICH_BLACK: [number, number, number, number] = [40, 30, 30, 100];

export const MM_PER_PT = 25.4 / 72;
export const PT_PER_PX = 72 / 96;
